import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { NEWS, newsSlug, newsBullets, newsDateLabel, newsHeading } from "./news";
import { Seo } from "../../shared/Seo";
import { useLandscapePhone } from "../../shared/useResponsive";
import styles from "./NewsPage.module.scss";
import { markNewsSeen } from "../../shared/announcement";

const SITE_URL = "https://achtungroyale.io";

// The News page as a version ledger: one entry per post, each a bar (version
// chip, date, a rule) over an optional title and one bulleted list. Titles
// link to the post's own page; a title-less entry gets a small "Link".
export default function NewsPage() {
  const app = useLandscapePhone();
  useEffect(() => { markNewsSeen(); }, []);
  const seo = (
    <Seo
      title="News · Achtung Royale"
      description="Latest updates, patch notes and announcements for Achtung Royale, the free browser Achtung, die Kurve! game."
      canonical={`${SITE_URL}/news/`}
    />
  );
  if (app) return <>{seo}<NewsReader /></>;
  return (
    <div className="section">
      {seo}
      <div className={styles.ledger}>
        {NEWS.map((n) => {
          const slug = newsSlug(n);
          return (
            <section key={slug} className={styles.group}>
              <div className={styles.ver}>
                {n.version ? (
                  <span className={styles.verNum}>v{n.version}</span>
                ) : n.tag ? (
                  <span className={styles.verNum}>{n.tag}</span>
                ) : null}
                <time className={styles.verDate} dateTime={n.date}>{newsDateLabel(n.date)}</time>
                <span className={styles.verRule} />
              </div>
              <article className={styles.post}>
                {n.title ? (
                  <h3 className={styles.postTitle}>
                    <Link className={styles.newsLink} to={`/news/${slug}`}>{n.title}</Link>
                  </h3>
                ) : null}
                <ul className={styles.bullets}>
                  {newsBullets(n).map((b, i) => <li key={i}>{b}</li>)}
                </ul>
                {!n.title ? (
                  <Link className={styles.permalink} to={`/news/${slug}`} aria-label="Link to this update">Link</Link>
                ) : null}
              </article>
            </section>
          );
        })}
      </div>
    </div>
  );
}

// The News on a landscape phone: the posts as a list on the left, the one you
// picked to read on the right. Picking a post swaps the pane in place (no
// route change), so the list keeps its scroll. `slug` opens on that post (the
// post's own page, /news/<slug>); otherwise the newest.
export function NewsReader({ slug }: { slug?: string }) {
  const [sel, setSel] = useState(() => Math.max(0, NEWS.findIndex((n) => newsSlug(n) === slug)));
  const post = NEWS[sel];
  const chip = (n: (typeof NEWS)[number]) => (n.version ? "v" + n.version : n.tag || null);
  return (
    <div className={styles.reader}>
      <div className={styles.readerList}>
        {NEWS.map((n, i) => (
          <button key={newsSlug(n)} type="button" className={i === sel ? `${styles.readerItem} ${styles.readerItemOn}` : styles.readerItem} aria-pressed={i === sel} onClick={() => setSel(i)}>
            <small>{chip(n) ? <i>{chip(n)}</i> : null}{newsDateLabel(n.date)}</small>
            <b>{newsHeading(n)}</b>
          </button>
        ))}
      </div>
      {post ? (
        <article className={styles.readerPane} key={sel}>
          <small>{chip(post) ? <i>{chip(post)}</i> : null}<time dateTime={post.date}>{newsDateLabel(post.date)}</time></small>
          <h3>{newsHeading(post)}</h3>
          <ul>{newsBullets(post).map((b, i) => <li key={i}>{b}</li>)}</ul>
        </article>
      ) : null}
    </div>
  );
}
